import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  StackDivider,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import {
  FacebookIcon,
  TwitterIcon,
  WLLink,
  YouTubeIcon,
} from '../../../design-library';
import FalconWatch from './images/FalconWatch.svg';
import WoodmenLogo from './images/WoodmenLife.svg';
import { NavGroup } from './types';

interface HeaderProps {
  nav: NavGroup[];
}

interface NavigationMenuProps {
  groups: NavGroup[];
}

const DesktopNavigationGroup: React.FC<NavGroup> = ({ label, href, items }) => {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onToggle();
    } else if (event.key === 'Tab' && isOpen) {
      onClose();
    }
  };
  return (
    <Box>
      <Menu gutter={0} isOpen={isOpen}>
        {href ? (
          <MenuButton as={WLLink} to={href} variant="plain">
            {label}
          </MenuButton>
        ) : (
          <MenuButton
            as={Button}
            variant="link"
            fontWeight="400"
            color="blue.800"
            rightIcon={<ChevronDownIcon />}
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            onKeyDown={handleOnKeyDown}
          >
            {label}
          </MenuButton>
        )}
        {items.length > 0 && (
          <MenuList onMouseEnter={onOpen} onMouseLeave={onClose}>
            {items.map((item, index) => (
              <MenuItem key={index} as={WLLink} to={item.href} variant="plain">
                {item.label}
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Menu>
    </Box>
  );
};

const MobileNavigationGroup: React.FC<NavGroup> = ({ label, href, items }) => {
  return (
    <AccordionItem>
      {href ? (
        <AccordionButton>
          <WLLink to={href} variant="plain">
            {label}
          </WLLink>
        </AccordionButton>
      ) : (
        <>
          <AccordionButton>
            {label}
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <VStack
              align="start"
              divider={<StackDivider borderColor="gray.300" />}
            >
              {items.map((item, index) => (
                <Box
                  as={WLLink}
                  key={index}
                  to={item.href}
                  variant="plain"
                  width="100%"
                >
                  {item.label}
                </Box>
              ))}
            </VStack>
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};

const NavigationMenu: React.FC<NavigationMenuProps> = ({ groups }) => (
  <>
    <HStack
      as="nav"
      display={['none', null, 'inherit']}
      width="full"
      marginTop="12px"
      justify="flex-end"
      spacing={4}
    >
      {groups.map((group, index) => (
        <DesktopNavigationGroup
          key={index}
          label={group.label}
          href={group.href}
          items={group.items}
        />
      ))}
    </HStack>
    <VStack as="nav" display={[null, null, 'none']} width="full">
      <Accordion allowToggle allowMultiple>
        <AccordionItem>
          <AccordionButton>
            Menu
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Accordion allowToggle allowMultiple>
              {groups.map((group, index) => (
                <MobileNavigationGroup
                  key={index}
                  label={group.label}
                  href={group.href}
                  items={group.items}
                />
              ))}
            </Accordion>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </VStack>
  </>
);

// fyi: removed variant="darkIconNoBackground" from all icons. Need to further research why this worked prior to 3/1/23
// looking at the logic makes me believe it never should have worked at all - THW
const Header: React.FC<HeaderProps> = ({ nav }) => {
  return (
    <header>
      <Box width="full" height="48px" backgroundColor="blue.200">
        <HStack float="right" spacing={4} height="full" paddingX={4}>
          <WLLink
            className="SocialLinks"
            to="https://www.facebook.com/WoodmenLife"
            target="_blank"
            variant="plain"
          >
            <FacebookIcon boxSize="30px" />
          </WLLink>
          <WLLink
            className="SocialLinks"
            to="https://twitter.com/WoodmenLife"
            target="_blank"
            variant="plain"
          >
            <TwitterIcon boxSize="30px" />
          </WLLink>
          <WLLink
            className="SocialLinks"
            to="https://www.youtube.com/user/ProducerWOW"
            target="_blank"
            variant="plain"
          >
            <YouTubeIcon boxSize="30px" />
          </WLLink>
        </HStack>
      </Box>

      <Stack
        width="full"
        direction={['column', null, 'row']}
        flexFlow={['column nowrap', null, 'row nowrap']}
        justify={['center', null, 'space-between']}
        align="center"
        spacing={[0, 0, 4, 8]}
        paddingX={{ lg: 4 }}
      >
        <Stack
          width="full"
          direction={['column', null, 'row']}
          align="center"
          spacing={[0, 0, 2, 4]}
          paddingY={[2, null, 8]}
        >
          <WLLink to="/" variant="plain">
            <Image
              width={250}
              className="WLLogoLink"
              src={WoodmenLogo as any}
              alt="WoodmenLife Homepage Link"
            />
          </WLLink>
          <WLLink to="/falcons/" variant="plain">
            <Image
              width={250}
              src={FalconWatch as any}
              alt="FalconWatch Homepage Link"
            />
          </WLLink>
        </Stack>

        <NavigationMenu groups={nav} />
      </Stack>

      <hr className="HeaderRule hidden-tablet-wide-down" />
    </header>
  );
};

export default Header;
